<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TemplatesPicker from './TemplatesPicker.vue';
import TemplateParser from 'dashboard/components-next/content-templates/ContentTemplateParser.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  inboxId: {
    type: Number,
    required: true,
  },
  channel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['onSend', 'cancel', 'update:show']);

const { t } = useI18n();

const selectedTemplate = ref(null);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const modalHeaderContent = computed(() => {
  return selectedTemplate.value
    ? t('CONTENT_TEMPLATES.MODAL.TEMPLATE_SELECTED_SUBTITLE', {
        templateName: selectedTemplate.value.friendly_name,
      })
    : t('CONVERSATION.REPLYBOX.META_TEMPLATES_SUBTITLE');
});

const pickTemplate = template => {
  selectedTemplate.value = template;
};

const resetTemplate = () => {
  selectedTemplate.value = null;
};

const onSendMessage = payload => {
  emit('onSend', {
    ...payload,
    metaTemplate: {
      id: selectedTemplate.value.id,
      channel: props.channel,
      additionalAttributes: selectedTemplate.value.additional_attributes || {},
    },
    templateParams: {
      ...payload.templateParams,
      message_template_id: selectedTemplate.value.id,
      channel: props.channel,
      template_type: 'template',
    },
  });
};

const onClose = () => emit('cancel');
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="modal-big">
    <woot-modal-header
      :header-title="$t('CONVERSATION.REPLYBOX.META_TEMPLATES')"
      :header-content="modalHeaderContent"
    />
    <div class="px-8 py-6 row">
      <TemplatesPicker
        v-if="!selectedTemplate"
        :inbox-id="inboxId"
        :channel="channel"
        @on-select="pickTemplate"
      />
      <TemplateParser
        v-else
        :template="selectedTemplate"
        @reset-template="resetTemplate"
        @send-message="onSendMessage"
      >
        <template #actions="{ sendMessage, resetTemplate: goBack, disabled }">
          <div class="flex gap-2 mt-6">
            <Button
              :label="$t('CONTENT_TEMPLATES.PARSER.GO_BACK_LABEL')"
              color="slate"
              variant="faded"
              class="flex-1"
              @click="goBack"
            />
            <Button
              :label="$t('CONTENT_TEMPLATES.PARSER.SEND_MESSAGE_LABEL')"
              class="flex-1"
              :disabled="disabled"
              @click="sendMessage"
            />
          </div>
        </template>
      </TemplateParser>
    </div>
  </woot-modal>
</template>
